import { moment } from '@/modules/moment'

/**
 * میکسین مشترک صفحات گزارش.
 *
 * مسئولیت‌ها:
 *  · نگه‌داشتن وضعیت فیلتر (بازه، حالت مقایسه، فقط پرداخت‌شده، بازه‌بندی)
 *  · فراخوانی اندپوینت‌ها و مدیریت loading بدون پرش لِی‌اوت
 *  · فرمت‌کردن اعداد بر اساس متادیتای متریک که خود بک‌اند می‌فرستد
 *  · خروجی CSV از هر جدول
 *
 * قرارداد: بک‌اند تاریخ را میلادی می‌گیرد و می‌دهد؛ تبدیل شمسی فقط برای نمایش
 * است. پس هر مقداری که به API می‌رود با فرمت YYYY-MM-DD میلادی می‌رود.
 */
export default {
  data() {
    return {
      // پیش‌فرض: ۳۰ روز اخیر، مقایسه با دوره‌ی قبل
      filter: {
        FromDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
        ToDate: moment().format('YYYY-MM-DD'),
        Compare: 1, // PreviousPeriod
        CompareFromDate: null,
        CompareToDate: null,
        Interval: null,
        PaidOnly: true,
      },
      preset: 'last30',
      reportLoading: {},
    }
  },

  computed: {
    /**
     * چون رنگ و تم نمودار به پوسته وابسته است و کاربر می‌تواند وسط کار
     * پوسته را عوض کند، از استور می‌خوانیم تا نمودارها واکنشی بمانند.
     */
    skin() {
      return this.$store.state.appConfig.layout.skin
    },

    isAnyLoading() {
      return Object.values(this.reportLoading).some(Boolean)
    },

    /** برچسب خوانای بازه‌ی فعلی برای عنوان و خروجی CSV */
    rangeLabel() {
      return `${this.toJalali(this.filter.FromDate)} تا ${this.toJalali(this.filter.ToDate)}`
    },
  },

  methods: {
    // ---------------------------------------------------------------- fetching

    /**
     * یک گزارش را می‌گیرد و در کلید مشخص‌شده روی this می‌نشاند.
     * در زمان رفرش، مقدار قبلی نگه داشته می‌شود (نه اسکلتون) تا صفحه نپرد.
     */
    fetchReport(key, url, extra = null) {
      this.$set(this.reportLoading, key, true)

      const body = { ...this.filter, ...(extra || {}) }

      return this.$http.post(url, body)
        .then(res => {
          this[key] = res.data
          return res.data
        })
        .catch(() => {
          // پیام خطا را اینترسپتور axios نشان می‌دهد؛ اینجا فقط
          // مقدار قبلی را دست‌نخورده می‌گذاریم
          return null
        })
        .finally(() => {
          this.$set(this.reportLoading, key, false)
        })
    },

    loading(key) {
      return !!this.reportLoading[key]
    },

    /** هر صفحه‌ی گزارش این را override می‌کند */
    refresh() {},

    // ------------------------------------------------------------ range preset

    applyPreset(preset) {
      this.preset = preset

      const today = moment()
      let from = today.clone()
      let to = today.clone()

      switch (preset) {
        case 'today':
          break
        case 'yesterday':
          from = today.clone().subtract(1, 'days')
          to = from.clone()
          break
        case 'last7':
          from = today.clone().subtract(6, 'days')
          break
        case 'last30':
          from = today.clone().subtract(29, 'days')
          break
        case 'last90':
          from = today.clone().subtract(89, 'days')
          break
        case 'thisMonth':
          from = today.clone().startOf('jMonth')
          break
        case 'lastMonth':
          from = today.clone().subtract(1, 'jMonth').startOf('jMonth')
          to = from.clone().endOf('jMonth')
          break
        case 'thisYear':
          from = today.clone().startOf('jYear')
          break
        case 'lastYear':
          from = today.clone().subtract(1, 'jYear').startOf('jYear')
          to = from.clone().endOf('jYear')
          break
        default:
          return
      }

      this.filter.FromDate = from.format('YYYY-MM-DD')
      this.filter.ToDate = to.format('YYYY-MM-DD')

      this.refresh()
    },

    // -------------------------------------------------------------- formatting

    toJalali(value, format = 'jYYYY/jMM/jDD') {
      if (!value) return ''
      return moment(value).format(format)
    },

    /**
     * فرمت عدد بر اساس نوعی که بک‌اند اعلام کرده.
     * قیمت‌ها به تومان و با جداکننده‌ی هزارگان؛ اعداد بزرگ خلاصه می‌شوند
     * تا کارت KPI سرریز نکند.
     */
    formatMetric(value, format) {
      if (value === null || value === undefined) return '—'

      switch (format) {
        case 'price':
          return this.formatPrice(value)
        case 'percent':
          return `${this.round(value, 1)}٪`
        case 'duration':
          return `${this.formatNumber(Math.round(value))} ms`
        default:
          return this.formatNumber(value)
      }
    },

    formatNumber(value) {
      if (value === null || value === undefined) return '—'
      const rounded = Math.abs(value % 1) > 0 ? this.round(value, 2) : value
      return String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    formatPrice(value) {
      return `${this.formatNumber(Math.round(value))} تومان`
    },

    /** خلاصه‌سازی برای جای تنگ: ۱۲.۳ م / ۴۵۶ ه */
    formatCompact(value, format) {
      if (value === null || value === undefined) return '—'

      if (format === 'percent') return `${this.round(value, 1)}٪`

      const abs = Math.abs(value)
      let text

      if (abs >= 1000000000) text = `${this.round(value / 1000000000, 1)} میلیارد`
      else if (abs >= 1000000) text = `${this.round(value / 1000000, 1)} میلیون`
      else if (abs >= 1000) text = `${this.round(value / 1000, 1)} هزار`
      else text = this.formatNumber(value)

      return format === 'price' ? `${text} تومان` : text
    },

    round(value, digits) {
      const factor = 10 ** digits
      return Math.round(value * factor) / factor
    },

    /** رنگ‌بندی دلتا: جهت «خوب» به متریک بستگی دارد، نه به علامت عدد */
    deltaVariant(delta, higherIsBetter = true) {
      if (delta === null || delta === undefined || delta === 0) return 'secondary'
      const positive = delta > 0
      return (positive === higherIsBetter) ? 'success' : 'danger'
    },

    deltaText(delta) {
      if (delta === null || delta === undefined) return null
      const sign = delta > 0 ? '+' : ''
      return `${sign}${this.round(delta, 1)}٪`
    },

    // ------------------------------------------------------------------ export

    /**
     * خروجی CSV. با BOM نوشته می‌شود تا اکسل فارسی را درست باز کند —
     * بدون BOM ستون‌های فارسی در اکسل ویندوز به‌هم می‌ریزند.
     */
    exportCsv(filename, columns, rows) {
      const header = columns.map(c => this.csvCell(c.label)).join(',')

      const body = rows.map(row => columns
        .map(c => this.csvCell(typeof c.value === 'function' ? c.value(row) : row[c.value]))
        .join(','))
        .join('\n')

      const csv = `﻿${header}\n${body}`

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      link.href = URL.createObjectURL(blob)
      link.download = `${filename}-${this.filter.FromDate}_${this.filter.ToDate}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },

    csvCell(value) {
      if (value === null || value === undefined) return ''
      const text = String(value)
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    },
  },
}
